import { useState, useEffect, useCallback, useMemo } from 'react'
import isEqual from 'lodash/isEqual'

import { SUCCESS_RESPONSE_TYPE } from 'api/constants'
import client from 'api/client'
import { resolveCurrentWorkspace } from 'utils'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { BLACKLISTED_DOMAINS } from './utils'

import { Spinner } from 'common/Spinner/Spinner'
import ManageMembers from './ManageMembers/ManageMembers'
import AllowedDomains from './AllowedDomains/AllowedDomains'

import Styles from './Members.module.scss'
import SettingsStyles from 'components/Settings/SettingsModal.module.scss'

import { SettingsGuest, SettingsMember } from 'types'
import { InviteLink } from './InviteUrl/InviteLink'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Heading } from 'blaze-ui/Heading/Heading'
import { useAuth } from 'hooks/UseAuth'
import { useCurrentWorkspace } from 'hooks/useCurrentWorkspace'
import { useFlipperFlag } from 'hooks/useFlipperFlag'
import { Modal } from 'almanac-ui/Modal'
type SelectOptions = ({ value: string | undefined; label: string | undefined } | undefined)[]

export const Members = () => {
  const isSettingsModalUpdateEnabled = useFlipperFlag('settings_modal_update')
  const auth = useAuth()
  const workspace = useCurrentWorkspace()
  const [users, setUsers] = useState<SettingsMember[]>([])
  const [guests, setGuests] = useState<SettingsGuest[]>([])
  const { isMobile } = useResponsiveDesign()

  const [domainOptions, setDomainOptions] = useState<SelectOptions>([])
  const [selectedDomains, setSelectedDomains] = useState<SelectOptions>([])
  const [domainsDirty, setDomainsDirty] = useState<boolean>(false)

  const loadData = useCallback(() => {
    if (!auth) return

    const load = async () => {
      const [membersResp, guestsResp] = await Promise.all([
        client.getWorkspaceMembers(auth.company_key),
        client.getWorkspaceGuests(auth.company_key),
      ])

      if (membersResp.type === SUCCESS_RESPONSE_TYPE) {
        const { members } = membersResp.data.data
        setUsers(members)
      }

      if (guestsResp.type === SUCCESS_RESPONSE_TYPE) {
        setGuests(guestsResp.data.guests)
      }
    }

    load()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, workspace?.members_count])

  /**
   * Collect all existing members' domains that are not on the blacklist
   * - format them for use in Select {value: xyz, label: xyz}
   */
  const formattedMemberDomains = useMemo(() => {
    if (!users || users.length < 1) return []

    const activeUsers = users.filter((user) => user.type === 'User')
    const emailDomains = [...new Set(activeUsers.map((u) => u.email?.split('@')?.pop()))]

    return emailDomains
      .sort()
      .map((domain) => (!domain || BLACKLISTED_DOMAINS.includes(domain) ? undefined : { value: domain, label: domain }))
      .filter((option) => !!option)
  }, [users])

  useEffect(() => {
    resolveCurrentWorkspace()
    loadData()
  }, [loadData])

  /**
   * Set domain options and selections
   */
  useEffect(() => {
    const formattedWorkspaceDomains = !!workspace
      ? workspace.auto_membership_domains.map((domain) => ({ value: domain, label: domain }))
      : []

    setDomainOptions(formattedMemberDomains)
    setSelectedDomains(formattedWorkspaceDomains)
  }, [workspace, users, formattedMemberDomains])

  /**
   * Update domains dirty state when selected domains change
   */
  useEffect(() => {
    const selectedDomainsArr = selectedDomains
      .map((option: any) => option.label)
      .slice()
      .sort()
    const originalDomainsArr = workspace?.auto_membership_domains.slice().sort() || []
    const originalSelection = isEqual(selectedDomainsArr, originalDomainsArr)

    setDomainsDirty(!originalSelection)
  }, [selectedDomains, workspace?.auto_membership_domains])

  /**
   * Handle saving domains
   */
  const handleSaveDomains = async () => {
    if (!workspace) return
    const domains = selectedDomains.map((domain) => domain?.value)

    workspace.auto_membership_domains = domains
    const res = await client.updateWorkspaceSettings(
      {
        workspace: {
          auto_membership_domains: domains,
        },
      },
      workspace.key,
    )

    if (res.type === SUCCESS_RESPONSE_TYPE) {
      setDomainsDirty(false)
      showToastMessage('Your workspace allowed domains have been updated!', 'success')
    } else {
      showToastMessage("Couldn't update your workspace allowed domains, please try again later", 'error')
    }
  }

  /**
   * Handle selecting domains
   */
  const handleDomainSelect = (domains: any) => {
    setSelectedDomains(domains)
  }

  if (!workspace) return <></>
  if (!users.length) return <Spinner className={SettingsStyles.spinner} />

  return (
    <>
      {!isMobile && !isSettingsModalUpdateEnabled && (
        <header className={SettingsStyles.subHeader}>
          <Heading level={3}>Members</Heading>
        </header>
      )}
      {!isMobile && isSettingsModalUpdateEnabled && (
        <Modal.Header title="Members" className={SettingsStyles.newHeader} />
      )}
      <div className={Styles.membersContainer}>
        {/* Invite Url */}
        <InviteLink workspace={workspace} />

        {/* Allowed Domains */}
        <AllowedDomains
          options={domainOptions}
          dirty={domainsDirty}
          handleSelect={handleDomainSelect}
          handleSave={handleSaveDomains}
          selected={selectedDomains}
        />

        {/* Workspace Members */}
        <div className={SettingsStyles.settingsSection}>
          <div className={Styles.headline}>
            <Heading level={3}>{auth?.admin ? 'Workspace Members' : 'Members'}</Heading>
          </div>
          <ManageMembers auth={auth} setUsers={setUsers} reloadUsers={loadData} users={users} guests={guests} />
        </div>
      </div>
    </>
  )
}
